import React from 'react';
import { Plus, PenLine, Trash2, LayoutTemplate, ArrowRight, X } from 'lucide-react';
import type { MarketingEmailTemplate } from '../../../models/MasterAdmin/marketingEmail';
import {
  meEmptyStateClass,
  meGhostBtnClass,
  meMainPanelClass,
  mePanelHeaderClass,
  mePanelTitleClass,
  mePrimaryBtnClass,
  meSecondaryBtnClass,
} from './marketingEmailUi';

interface TemplatesPanelProps {
  templates: MarketingEmailTemplate[];
  onSelect: (template: MarketingEmailTemplate) => void;
  onCreate: () => void;
  onEdit: (template: MarketingEmailTemplate) => void;
  onDelete: (id: string) => void;
  onClose?: () => void;
}

const TemplatesPanel: React.FC<TemplatesPanelProps> = ({
  templates,
  onSelect,
  onCreate,
  onEdit,
  onDelete,
  onClose,
}) => {
  return (
    <div className={meMainPanelClass}>
      <div className={mePanelHeaderClass}>
        <div className="flex items-center gap-2">
          <LayoutTemplate className="h-5 w-5 text-primary" />
          <h2 className={mePanelTitleClass}>Templates</h2>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onCreate} className={mePrimaryBtnClass}>
            <Plus className="h-4 w-4" />
            Create
          </button>
          {onClose && (
            <button type="button" onClick={onClose} className={meGhostBtnClass}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        {templates.length === 0 ? (
          <div className={meEmptyStateClass}>
            <LayoutTemplate className="h-12 w-12 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500">No templates yet. Create one to reuse in compose.</p>
            <button type="button" onClick={onCreate} className={mePrimaryBtnClass}>
              Create template
            </button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {templates.map((tpl) => (
              <div
                key={tpl.id}
                className="group flex flex-col rounded-xl border border-gray-200/80 bg-gray-50/30 p-4 transition hover:border-primary/40 hover:shadow-sm dark:border-gray-800 dark:bg-gray-950/30 dark:hover:border-primary/40"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">{tpl.name}</h3>
                {tpl.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{tpl.description}</p>
                )}
                <p className="mt-2 truncate text-sm text-gray-700 dark:text-gray-300">{tpl.subject}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" onClick={() => onSelect(tpl)} className={mePrimaryBtnClass + ' flex-1 text-xs'}>
                    Use in compose
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => onEdit(tpl)} className={meSecondaryBtnClass}>
                    <PenLine className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(tpl.id)}
                    className={meSecondaryBtnClass + ' text-red-600 dark:text-red-400'}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplatesPanel;

export const TemplatesPickerModal: React.FC<{
  open: boolean;
  templates: MarketingEmailTemplate[];
  onSelect: (template: MarketingEmailTemplate) => void;
  onClose: () => void;
}> = ({ open, templates, onSelect, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="max-h-[85vh] w-full max-w-3xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        <TemplatesPanel
          templates={templates}
          onSelect={(tpl) => { onSelect(tpl); onClose(); }}
          onCreate={onClose}
          onEdit={() => {}}
          onDelete={() => {}}
          onClose={onClose}
        />
      </div>
    </div>
  );
};
